import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tooltip, TooltipArrow, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { CircleHelp } from "lucide-react";
import { useState } from "react";

// Steps shown to users who don't know where their Xactnet address lives in Xactimate.
const FIND_XACTNET_STEPS = [
  "Log in to your Xactimate account.",
  "Click on the account icon in the top-right of the screen.",
  "See the Xactnet address field listed for your account.",
  "Enter the text after the @ in the address into Rebuild.",
];

// Drop the supplied screenshot at this path in `public/` to render it in the dialog.
const XACTNET_HELP_IMAGE_SRC = "/xactnet-address-help.png";

type FindXactnetAddressDialogProps = {
  /** Positioning hook for the trigger (e.g. absolute placement inside an input). */
  className?: string;
};

export function FindXactnetAddressDialog({ className }: FindXactnetAddressDialogProps) {
  // Track image load failures so a missing/not-yet-supplied screenshot doesn't show a broken icon.
  const [imageFailed, setImageFailed] = useState(false);

  return (
    <Dialog>
      <TooltipProvider>
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <DialogTrigger
              aria-label="Find your Xactnet address"
              className={cn(
                "text-muted-foreground hover:text-foreground focus-visible:ring-ring inline-flex rounded-sm transition-colors focus-visible:ring-2 focus-visible:outline-none",
                className,
              )}
            >
              <CircleHelp className="size-4" aria-hidden="true" />
            </DialogTrigger>
          </TooltipTrigger>
          {/* overflow-visible so the arrow isn't clipped by the shared content's overflow-hidden. */}
          <TooltipContent side="top" sideOffset={8} className="overflow-visible">
            <i>Where is my Xactnet address?</i>
            <TooltipArrow className="fill-popover" width={11} height={5} />
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Find your Xactnet address</DialogTitle>
          <DialogDescription><i>Your Xactnet address is used to export estimates to Xactimate.</i></DialogDescription>
        </DialogHeader>

        <ol className="text-foreground flex list-decimal flex-col gap-2 pl-5 text-sm">
          {FIND_XACTNET_STEPS.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>

        {!imageFailed && (
          <img
            src={XACTNET_HELP_IMAGE_SRC}
            alt="Where to find the Xactnet address field in Xactimate"
            className="border-border w-full rounded-md border"
            onError={() => setImageFailed(true)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
